import { useCallback, useState } from "react";
import SectionTitle from "../../components/SectionTitle/SectionTitle";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebaseConfig";
import { useUserStore } from "../../store";
import Loading from "../../components/Reusable/Loading";
import FeeSelector from "../../components/Reusable/FeeSelector";
import { FeeObject, FeeRateOptions } from "../../types";
import { signTransaction } from "sats-connect";
import { setNotificationState } from "../../notification";
import GeneralNotification from "../../notification/Notifications/GeneralNotification";

type DLResponse = {
  base64: string;
  inputs: number;
} | null;

const RsicClaim = () => {
  const btcAddress = useUserStore(state => state.btcAddress);
  const ordAddress = useUserStore(state => state.ordAddress);
  const [claimEscrowIds, setClaimEscrowIds] = useState<any[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [dlResponse, setDlResponse] = useState<DLResponse | null>(null);
  const [selectedLoanId, setSelectedLoanId] = useState(null);
  const [selectedFeeRate, setSelectedFeeRate] = useState<FeeObject>({
    label: FeeRateOptions.Medium,
    value: 0,
  } as FeeObject);
  const [isSuccess, setIsSuccess] = useState<boolean | null>(null);

  const getRsicClaimLoans = httpsCallable(functions, "getRsicClaimLoans");
  const prepareRsicClaim = httpsCallable(functions, "prepareRsicClaim");
  const broadcastRsicClaim = httpsCallable(functions, "broadcastRsicClaim");

  const handleGetLoansClick = useCallback(async () => {
    setIsLoading(true);

    try {
      const response: any = await getRsicClaimLoans({ id: ordAddress });
      setClaimEscrowIds(response.data);
    } catch {
      setNotificationState(() => (
        <GeneralNotification text={"Error"} variant="error" />
      ));
    }

    setIsLoading(false);
  }, [ordAddress]);

  const handlePrepareClaim = useCallback(
    async selectedId => {
      if (selectedFeeRate.value === 0) {
        setNotificationState(() => (
          <GeneralNotification text={"Need a fee rate"} variant="error" />
        ));
        return;
      }

      setIsLoading(true);

      try {
        const response: any = await prepareRsicClaim({
          loanId: selectedId,
          feeRate: selectedFeeRate.value,
        });
        const data = response.data;
        if (data.result === "All RSICs Claimed") {
          setNotificationState(() => (
            <GeneralNotification text={"All RSICs Claimed from Escrow"} variant="error" />
          ));
          setIsLoading(false);
          setSelectedLoanId(null);
          return;
        }
        setDlResponse(data);
        setSelectedLoanId(selectedId);
      } catch {
        setNotificationState(() => (
          <GeneralNotification text={"Error"} variant="error" />
        ));
      }

      setIsLoading(false);
    },
    [selectedFeeRate.value],
  );

  const handleClaim = useCallback(async () => {
    setIsLoading(true);

    if (!dlResponse?.inputs || !dlResponse?.base64) {
      setNotificationState(() => (
        <GeneralNotification text={"Error"} variant="error" />
      ));
      return;
    }

    const signingIndexes = Array.from(
      { length: dlResponse.inputs - 1 },
      (_, i) => i + 1,
    );

    const signPsbtOptions = {
      payload: {
        network: {
          type: "Mainnet",
        },
        message: "Sign Transaction",
        psbtBase64: dlResponse.base64,
        inputsToSign: [
          {
            address: btcAddress,
            signingIndexes,
          },
        ],
      },
      onFinish: async (result: any) => {
        const response: any = await broadcastRsicClaim({
          loanId: selectedLoanId,
          base64: result.psbtBase64,
        });
        console.log("response", response);
        if (response.data.txid) {
          setNotificationState(() => (
            <GeneralNotification text={"Success"} variant="success" />
          ));
        }
        setIsLoading(false);
        setSelectedLoanId(null);
        setDlResponse(null);
        setClaimEscrowIds(null);
      },
    };

    // @ts-ignore
    await signTransaction(signPsbtOptions);
  }, [selectedLoanId, btcAddress, dlResponse]);

  return (
    <div className={"sm:mt-10 mt-6 justify-center"}>
      <div className="max-w-[1400px] w-full">
        <div className="flex w-full justify-between items-center">
          <SectionTitle
            title="RSIC Claim"
            learnMore=""
            infoText={
              <>
                <p className="text-gray-400 font-semibold text-xs sm:text-sm mt-2">
                  We found some RSICs that where airdropped to one or more of
                  your previous loans.
                  <br />
                  Please follow the steps below to claim.
                </p>
              </>
            }
          />
        </div>
      </div>
      <div className="flex justify-center items-center">
        <div className="sm:mt-7 mt-5 mb-10">
          {isLoading ? (
            <div className="h-[50px]">
              <Loading />
            </div>
          ) : claimEscrowIds === null ? (
            <button
              onClick={handleGetLoansClick}
              className="bg-orange-500/90 hover:bg-orange-400 transition-colors border border-black/20 text-zinc-800 font-bold p-2 py-1 rounded-md sm:mt-3 mt-2 sm:text-base text-sm cursor-pointer px-4"
            >
              Check My Escrows
            </button>
          ) : claimEscrowIds.length === 0 ? (
            <p className="text-gray-50 font-extrabold sm:text-2xl text-xl flex items-center gap-2">
              No RSICs to Claim
            </p>
          ) : dlResponse !== null ? (
            <button
              onClick={handleClaim}
              className="block mx-auto bg-orange-500/90 hover:bg-orange-400 transition-colors border border-black/20 text-zinc-800 font-bold p-2 py-1 rounded-md sm:mt-3 mt-2 sm:text-base text-sm cursor-pointer px-4"
            >
              Sign and Unlock RSIC!
            </button>
          ) : (
            <div className="mx-auto text-center">
              <p className="text-gray-50 font-extrabold sm:text-2xl text-xl flex items-center gap-2">
                Found Escrows with RSICs!
              </p>
              <FeeSelector
                selectedFeeRate={selectedFeeRate}
                setSelectedFeeRate={setSelectedFeeRate}
              />
              {selectedFeeRate.value !== 0 &&
                claimEscrowIds.map((id, i) => (
                  <button
                    onClick={() => handlePrepareClaim(id)}
                    className="bg-orange-500/90 hover:bg-orange-400 transition-colors border border-black/20 text-zinc-800 font-bold p-2 py-1 rounded-md sm:mt-3 mt-2 sm:text-base text-sm cursor-pointer px-4"
                  >
                    Prepare Transaction For Escrow {i + 1}
                  </button>
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RsicClaim;
